import moment from 'moment';

class AddFundingObligationModalController {
  constructor(
    $uibModalInstance,
    $http,
    $rootScope,
    $scope,
    toastr,
    contractId,
    periodsOfPerformance,
    projects,
    workTypeOptions,
    costElementOptions,
    costBased,
    obligation
  ) {
    'ngInject';

    this.$uibModalInstance = $uibModalInstance;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$scope = $scope;
    this.toastr = toastr;
    this.contractId = contractId;
    this.periodsOfPerformance = periodsOfPerformance || [];
    this.projects = projects || [];
    this.workTypeOptions = workTypeOptions || [];
    this.costElementOptions = costElementOptions || [];
    this.costBased = !!costBased;
    this.obligation = obligation || null;
    this.isEdit = !!(obligation && obligation.id);
    this.saving = false;

    this.fundingBasisOptions = this.buildFundingBasisOptions();

    this.model = {
      periodOfPerformanceId: this.isEdit ? obligation.periodOfPerformanceId : null,
      taskOrderId: this.isEdit ? obligation.taskOrderId : null,
      workType: this.isEdit ? obligation.workType : null,
      fundingBasis: this.isEdit ? (obligation.fundingBasis || null) : null,
      clin: this.isEdit ? (obligation.clin || '') : '',
      slin: this.isEdit ? (obligation.slin || '') : '',
      acrn: this.isEdit ? (obligation.acrn || '') : '',
      costElement: this.isEdit ? obligation.costElement : null,
      basisUnitOfMeasure: this.isEdit ? (obligation.basisUnitOfMeasure || '') : '',
      obligatedAmount: this.isEdit ? obligation.obligatedAmount : null
    };

    this.$scope.$watch(() => this.model.periodOfPerformanceId, (newVal, oldVal) => {
      if (newVal === oldVal) {
        return;
      }
      const available = this.getAvailableProjects();
      const stillValid = available.some((project) => String(project.id) === String(this.model.taskOrderId));
      if (!stillValid) {
        this.model.taskOrderId = null;
      }
    });

    this.$scope.$watch(() => this.model.workType, (newVal, oldVal) => {
      if (newVal === oldVal) {
        return;
      }
      if (newVal !== 'LEVEL_OF_EFFORT') {
        this.model.fundingBasis = null;
      }
    });
  }

  buildFundingBasisOptions() {
    const options = [
      { value: 'CONTRACT_RATES', text: 'Contract Rates' }
    ];
    if (this.costBased) {
      options.unshift({ value: 'ACTUALS', text: 'Actuals' });
    }
    return options;
  }

  showFundingBasis() {
    return this.model.workType === 'LEVEL_OF_EFFORT';
  }

  getSelectedPeriodOfPerformance() {
    if (!this.model.periodOfPerformanceId) {
      return null;
    }
    return (this.periodsOfPerformance || []).find((pop) => String(pop.id) === String(this.model.periodOfPerformanceId)) || null;
  }

  isProjectWithinPeriodOfPerformance(project, pop) {
    if (!project || !pop || !pop.startDate || !pop.endDate) {
      return false;
    }
    if (!project.startDate || !project.completionDate) {
      return false;
    }

    const popStart = moment(pop.startDate).startOf('day');
    const popEnd = moment(pop.endDate).startOf('day');
    const projectStart = moment(project.startDate).startOf('day');
    const projectEnd = moment(project.completionDate).startOf('day');

    if (!popStart.isValid() || !popEnd.isValid() || !projectStart.isValid() || !projectEnd.isValid()) {
      return false;
    }

    return projectStart.isSameOrBefore(popEnd, 'day') && projectEnd.isSameOrAfter(popStart, 'day');
  }

  getAvailableProjects() {
    const pop = this.getSelectedPeriodOfPerformance();
    if (!pop) {
      return [];
    }
    return (this.projects || []).filter((project) => this.isProjectWithinPeriodOfPerformance(project, pop));
  }

  getApiUrl() {
    return `${this.$rootScope.apiHost}/api/admin/contracts/${this.contractId}/funding-obligations`;
  }

  save() {
    const form = this.$scope.fundingObligationForm;
    if (form) {
      form.$setSubmitted();
      if (form.$invalid) {
        this.toastr.error('Please complete all required fields', 'ERROR');
        return;
      }
    }

    if (!this.model.periodOfPerformanceId || !this.model.taskOrderId || !this.model.workType || !this.model.costElement) {
      this.toastr.error('Option Year, Task Order, Type of Work, and Cost Element are required', 'ERROR');
      return;
    }
    if (!this.model.clin || !String(this.model.clin).trim() || !this.model.acrn || !String(this.model.acrn).trim()) {
      this.toastr.error('CLIN and ACRN are required', 'ERROR');
      return;
    }
    if (this.model.obligatedAmount == null || this.model.obligatedAmount === '') {
      this.toastr.error('Obligated Amount is required', 'ERROR');
      return;
    }
    if (this.showFundingBasis()
        && this.model.fundingBasis === 'ACTUALS'
        && !this.costBased) {
      this.toastr.error('Actuals funding basis is only allowed for cost-based contracts', 'ERROR');
      return;
    }

    const available = this.getAvailableProjects();
    const selectedStillValid = available.some((project) => String(project.id) === String(this.model.taskOrderId));
    if (!selectedStillValid) {
      this.toastr.error('Selected Task Order must fall within the Option Year period of performance', 'ERROR');
      return;
    }

    const payload = {
      periodOfPerformanceId: Number(this.model.periodOfPerformanceId),
      taskOrderId: Number(this.model.taskOrderId),
      workType: this.model.workType,
      fundingBasis: this.showFundingBasis() ? (this.model.fundingBasis || null) : null,
      clin: String(this.model.clin).trim(),
      slin: this.model.slin == null ? '' : String(this.model.slin).trim(),
      acrn: String(this.model.acrn).trim(),
      costElement: this.model.costElement,
      basisUnitOfMeasure: this.model.basisUnitOfMeasure == null ? '' : String(this.model.basisUnitOfMeasure).trim(),
      obligatedAmount: Number(this.model.obligatedAmount)
    };

    this.saving = true;
    const request = this.isEdit
      ? this.$http.patch(`${this.getApiUrl()}/${this.obligation.id}`, payload, { withCredentials: true })
      : this.$http.post(this.getApiUrl(), payload, { withCredentials: true });

    request.then((success) => {
      this.toastr.success(this.isEdit ? 'Funding obligation updated' : 'Funding obligation added', 'SUCCESS');
      this.$uibModalInstance.close(success.data);
    }, (failure) => {
      const message = (failure && failure.data && failure.data.message) || 'Failed to save funding obligation';
      this.toastr.error(message, 'ERROR');
    }).finally(() => {
      this.saving = false;
    });
  }

  cancel() {
    this.$uibModalInstance.dismiss();
  }
}

export default AddFundingObligationModalController;



// WEBPACK FOOTER //
// ./client/common/modals/addFundingObligationModal/addFundingObligationModal.controller.js